import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { AdminUserAvatar } from './AdminUserAvatar';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { setWebPlaceholderColor, getAdminSearchPlaceholderColor } from '../../utils/setWebPlaceholderColor';
import { testerBadgeLabel } from '../../utils/testerAccounts';
import { TesterSwitchButtons } from '../TesterSwitcher';
import { useUser } from '../LoginPersistence';
import { canArchiveEmailExtras, groupUsersByEmail, pickKeeperAndExtras, usersSharingEmail } from '../../utils/duplicateUserAccounts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { contributionCountsByUser, contributionCountsFor } from '../../utils/adminUserCounts';

const TESTER_COLOR = '#9b59b6';

/** Purple "Tester · Admin" chip for the QA accounts (scripts/create-tester-accounts.cjs). */
function TesterBadge({ user, style }) {
  const label = testerBadgeLabel(user);
  if (!label) return null;
  return (
    <Text
      accessibilityLabel={`${label} account`}
      style={[{
        color: TESTER_COLOR,
        fontFamily: 'VastagoGrotesk',
        fontSize: 12,
        fontWeight: '700',
        backgroundColor: `${TESTER_COLOR}18`,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        alignSelf: 'flex-start',
      }, style]}
    >
      {label}
    </Text>
  );
}

const USERS_VIEW_MODE_KEY = 'vrc-admin-users-view-mode';
const USERS_COLUMNS_KEY = 'vrc-admin-users-columns';
const USERS_SEARCH_ID = 'admin-users-search';

function loadViewMode() {
  if (typeof window === 'undefined') return 'list';
  const saved = localStorage.getItem(USERS_VIEW_MODE_KEY);
  return saved === 'cards' ? 'cards' : 'list';
}

function loadColumnCount() {
  if (typeof window === 'undefined') return 1;
  const saved = parseInt(localStorage.getItem(USERS_COLUMNS_KEY), 10);
  return [1, 2, 3].includes(saved) ? saved : 1;
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatShortDate(value) {
  if (!value) return '—';
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function resolveUserType(user, userTypes) {
  return Object.values(userTypes).find((type) => type.value === user.userType) || userTypes.MEMBER;
}

export function AdminUsersList({
  users,
  organizations,
  userStats,
  userIdMismatches,
  verifierNames,
  userTypes,
  isUserOnline,
  onUserAction,
  onRevokeVerification,
  onApproveVerification,
  onMigrateUserIds,
  onUpdateSubscription,
  onArchiveUser,
  onArchiveEmailExtras,
  onOrganizationAction,
}) {
  const { styles: adminStyles, colors, isDarkMode } = useAdminThemeContext();
  const { user: signedInUser } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('signup');
  const [viewMode, setViewMode] = useState(loadViewMode);
  const [columnCount, setColumnCount] = useState(loadColumnCount);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [contributionCounts, setContributionCounts] = useState({});

  // Reports (grades) and tech specs given per user, from venue_reports (BUG-089).
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'venue_reports'))
      .then((snap) => {
        if (!cancelled) setContributionCounts(contributionCountsByUser(snap.docs.map((d) => d.data())));
      })
      .catch((error) => console.warn('Could not load report counts for users:', error));
    return () => { cancelled = true; };
  }, []);

  const searchPlaceholderColor = getAdminSearchPlaceholderColor(isDarkMode);

  useEffect(() => {
    setWebPlaceholderColor(USERS_SEARCH_ID, searchPlaceholderColor);
  }, [searchPlaceholderColor]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(USERS_VIEW_MODE_KEY, viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(USERS_COLUMNS_KEY, String(columnCount));
    }
  }, [columnCount]);

  const pillStyle = (active, accent = '#0bb5df') => ({
    backgroundColor: active ? accent : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 88,
    alignItems: 'center',
    justifyContent: 'center',
  });

  const pillTextStyle = (active) => ({
    color: active ? '#ffffff' : colors.textPrimary,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: active ? '600' : '500',
  });

  const columnBtnStyle = (active) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: active ? '#0bb5df' : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
  });

  const duplicateGroups = useMemo(() => groupUsersByEmail(users), [users]);
  const duplicateEmailSet = useMemo(
    () => new Set(duplicateGroups.map((group) => group.email)),
    [duplicateGroups]
  );

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return users
      .filter((user) => {
        const matchesSearch =
          !q ||
          user.firstName?.toLowerCase().includes(q) ||
          user.lastName?.toLowerCase().includes(q) ||
          user.email?.toLowerCase().includes(q) ||
          user.username?.toLowerCase().includes(q);

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && user.status !== 'suspended') ||
          (statusFilter === 'suspended' && user.status === 'suspended') ||
          (statusFilter === 'unactivated' && !user.activatedAt) ||
          (statusFilter === 'duplicates' && duplicateEmailSet.has(String(user.email || '').trim().toLowerCase())) ||
          (statusFilter === 'testers' && !!testerBadgeLabel(user));

        const matchesType = typeFilter === 'all' || user.userType === typeFilter;

        return matchesSearch && matchesStatus && matchesType;
      })
      .sort((a, b) => {
        if (sortBy === 'lastLogin') {
          const aDate = a.lastLogin?.toDate ? a.lastLogin.toDate() : a.lastLogin ? new Date(a.lastLogin) : new Date(0);
          const bDate = b.lastLogin?.toDate ? b.lastLogin.toDate() : b.lastLogin ? new Date(b.lastLogin) : new Date(0);
          return bDate - aDate;
        }
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
        return bDate - aDate;
      });
  }, [users, searchQuery, statusFilter, typeFilter, sortBy, duplicateEmailSet]);

  const cardsGridStyle =
    Platform.OS === 'web'
      ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gap: 16,
          width: '100%',
          alignItems: 'start',
        }
      : { gap: 16, width: '100%' };

  const cardWrapStyle =
    Platform.OS === 'web'
      ? { minWidth: 0, width: '100%' }
      : { width: columnCount === 1 ? '100%' : columnCount === 2 ? '48%' : '31%' };

  const statItems = [
    { label: 'Total Users', value: userStats.total },
    { label: 'Organizations', value: userStats.organizationAccounts },
    { label: 'Personal', value: userStats.personalAccounts },
    { label: 'Active', value: userStats.active },
    { label: 'Suspended', value: userStats.suspended },
    { label: 'Admins', value: userStats.admins },
    { label: 'ID Mismatches', value: Object.keys(userIdMismatches).length },
    { label: 'Duplicate emails', value: duplicateGroups.reduce((sum, group) => sum + group.users.length, 0) },
  ];

  const renderActionBtn = (label, bg, onPress, compact = false) => (
    <TouchableOpacity
      key={label}
      style={{
        backgroundColor: bg,
        paddingVertical: compact ? 8 : 10,
        paddingHorizontal: compact ? 10 : 14,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: compact ? 120 : 140,
        flexGrow: compact ? 1 : 0,
        flexBasis: compact ? '30%' : undefined,
      }}
      onPress={onPress}
    >
      <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontSize: compact ? 11 : 13, fontWeight: '600' }}>
        {label}
      </Text>
    </TouchableOpacity>
  );

  const renderUserActions = (user) => {
    const isVerified = user.verificationStatus === 'approved';
    const isSuspended = user.status === 'suspended';
    const hasMismatch = userIdMismatches[user.id];

    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        {isSuspended
          ? renderActionBtn('Activate', '#2ecc71', () => onUserAction(user.id, 'activate'), true)
          : renderActionBtn('Suspend', '#e74c3c', () => onUserAction(user.id, 'suspend'), true)}
        {isVerified
          ? renderActionBtn('Revoke Verification', '#f39c12', () => onRevokeVerification(user), true)
          : renderActionBtn('Verify User', '#0bb5df', () => onApproveVerification(user), true)}
        {hasMismatch && renderActionBtn('Fix ID Mismatch', '#e67e22', onMigrateUserIds, true)}
        {user.subscriptionStatus === 'active'
          ? renderActionBtn('Revoke Subscription', '#e74c3c', () => onUpdateSubscription(user, 'inactive'), true)
          : (
            <>
              {renderActionBtn('Grant VRC Member', '#2ecc71', () => onUpdateSubscription(user, 'active', 'vrc_member'), true)}
            </>
          )}
        {user.isPublic
          ? renderActionBtn('Make Private', '#e74c3c', () => onUserAction(user.id, 'makePrivate'), true)
          : renderActionBtn('Make Public', '#2ecc71', () => onUserAction(user.id, 'makePublic'), true)}
        {user.isCommunityMember
          ? renderActionBtn('Remove Community', '#e67e22', () => onUserAction(user.id, 'removeCommunityMember'), true)
          : renderActionBtn('Add to Community', '#16a085', () => onUserAction(user.id, 'makeCommunityMember'), true)}
        {renderActionBtn('Archive', '#95a5a6', () => onArchiveUser(user.id), true)}
        {canArchiveEmailExtras(pickKeeperAndExtras(usersSharingEmail(users, user), { keepId: user.id })).ok &&
          renderActionBtn(
            `Archive extras, keep this (${usersSharingEmail(users, user).length - 1})`,
            '#7f8c8d',
            () => onArchiveEmailExtras(user),
            true
          )}
      </View>
    );
  };

  const renderUserDetails = (user) => {
    const userType = resolveUserType(user, userTypes);
    const isVerified = user.verificationStatus === 'approved';
    const online = isUserOnline(user);
    const verifierName = verifierNames[user.verifiedBy] || 'Unknown Admin';

    return (
      <View style={{ gap: 8, marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.divider }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
          <Text style={[adminStyles.detailText, { flex: 1, minWidth: 160 }]}>
            Status:{' '}
            <Text style={{ color: user.status === 'suspended' ? '#e74c3c' : '#2ecc71', fontWeight: '600' }}>
              {(user.status || 'active').toUpperCase()}
            </Text>
          </Text>
          <Text style={adminStyles.detailText}>Joined: {formatDateTime(user.createdAt)}</Text>
          <Text style={adminStyles.detailText}>Last login: {formatDateTime(user.lastLogin)}</Text>
          <Text style={adminStyles.detailText}>Reports: {contributionCountsFor(contributionCounts, user.id).reports} · Tech specs: {contributionCountsFor(contributionCounts, user.id).techSpecs}</Text>
          <Text style={adminStyles.detailText}>Saved venues: {user.savedVenues?.length || 0}</Text>
          <Text style={adminStyles.detailText}>
            Privacy: {user.isPublic ? 'Public' : 'Private'} · Community: {user.isCommunityMember ? 'Yes' : 'No'}
            {user.subscriptionStatus === 'active'
              ? ` · Sub: ${user.subscriptionPlan === 'vrc_member' || user.subscriptionPlan === 'map_calendar' || user.subscriptionPlan === 'calendar_only' ? 'VRC Member' : 'Active'}`
              : ''}
          </Text>
        </View>
        {(user.occupation || user.workplace) && (
          <Text style={adminStyles.detailText}>
            {user.occupation}{user.workplace ? ` @ ${user.workplace}` : ''}
          </Text>
        )}
        {user.bio ? <Text style={adminStyles.detailText}>{user.bio}</Text> : null}
        {isVerified && (
          <Text style={[adminStyles.detailText, { color: colors.textMuted }]}>
            Verified {formatDateTime(user.verifiedAt)} by {verifierName}
          </Text>
        )}
        {user.socialLinks?.length > 0 && (
          <View>
            <Text style={[adminStyles.detailLabel, { marginBottom: 4 }]}>Social</Text>
            {user.socialLinks.filter(Boolean).map((link, i) => (
              <Text key={i} style={[adminStyles.detailText, { fontSize: 13 }]}>{link}</Text>
            ))}
          </View>
        )}
        {renderUserActions(user)}
      </View>
    );
  };

  const renderUserListRow = (user) => {
    const userType = resolveUserType(user, userTypes);
    const online = isUserOnline(user);
    const hasMismatch = userIdMismatches[user.id];
    const isExpanded = expandedUserId === user.id;
    const borderColor = hasMismatch ? '#f39c12' : userType.color;

    return (
      <View
        key={user.id}
        style={{
          backgroundColor: colors.cardBg,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: colors.cardBorder,
          borderLeftWidth: 4,
          borderLeftColor: borderColor,
          marginBottom: 8,
          overflow: 'hidden',
        }}
      >
        <TouchableOpacity
          onPress={() => setExpandedUserId(isExpanded ? null : user.id)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: 14,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <AdminUserAvatar user={user} size={44} />
          <View style={{ flex: 1, minWidth: 180 }}>
            <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontWeight: '700', fontSize: 16 }}>
              {user.username || 'No username'}
            </Text>
            <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13 }}>
              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
            </Text>
            <Text style={{ color: colors.textMuted, fontFamily: 'VastagoGrotesk', fontSize: 12 }} numberOfLines={1}>
              {user.email}
              {usersSharingEmail(users, user).length > 1
                ? ` · ${usersSharingEmail(users, user).length} profiles`
                : ''}
            </Text>
          </View>

          <View style={{ minWidth: 100, alignItems: 'flex-start' }}>
            <Text style={{
              color: userType.color,
              fontFamily: 'VastagoGrotesk',
              fontSize: 12,
              fontWeight: '700',
              backgroundColor: `${userType.color}18`,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 6,
            }}>
              {userType.label}
            </Text>
            <TesterBadge user={user} style={{ marginTop: 6 }} />
          </View>

          <View style={{ minWidth: 90, alignItems: 'flex-start' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: online ? '#2ecc71' : colors.textMuted,
              }} />
              <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 12 }}>
                {online ? 'Online' : 'Offline'}
              </Text>
            </View>
            <Text style={{
              color: user.status === 'suspended' ? '#e74c3c' : colors.textMuted,
              fontFamily: 'VastagoGrotesk',
              fontSize: 11,
              marginTop: 4,
              fontWeight: user.status === 'suspended' ? '700' : '400',
            }}>
              {(user.status || 'active').toUpperCase()}
            </Text>
          </View>

          <View style={{ minWidth: 100 }}>
            <Text style={{ color: colors.textMuted, fontFamily: 'VastagoGrotesk', fontSize: 11 }}>Last login</Text>
            <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 12 }}>
              {formatShortDate(user.lastLogin)}
            </Text>
          </View>

          <Text style={{ color: colors.textMuted, fontSize: 18, marginLeft: 4 }}>
            {isExpanded ? '▾' : '▸'}
          </Text>
        </TouchableOpacity>

        {isExpanded && (
          <View style={{ paddingHorizontal: 14, paddingBottom: 14 }}>
            {renderUserDetails(user)}
          </View>
        )}
      </View>
    );
  };

  const renderUserCard = (user) => {
    const userType = resolveUserType(user, userTypes);
    const online = isUserOnline(user);
    const hasMismatch = userIdMismatches[user.id];
    const showFull = columnCount === 1;

    return (
      <View
        key={user.id}
        style={[
          adminStyles.reportCard,
          {
            borderLeftColor: hasMismatch ? '#f39c12' : userType.color,
            borderLeftWidth: 4,
            marginBottom: 0,
          },
        ]}
      >
        <View style={[adminStyles.reportGrid, !showFull && { flexDirection: 'column' }]}>
          <View style={[adminStyles.columnLeft, !showFull && { minWidth: 0, width: '100%', flex: undefined }]}>
            <View style={[adminStyles.profileCard, { marginBottom: 12 }]}>
              <View style={adminStyles.profileHeader}>
                <View style={adminStyles.profileImageContainer}>
                  <AdminUserAvatar user={user} size={80} style={{ width: '100%', height: '100%', borderRadius: 40 }} />
                </View>
                <View style={adminStyles.profileInfo}>
                  <Text style={adminStyles.userName}>{user.username || 'N/A'}</Text>
                  <Text style={adminStyles.userFullName}>{user.firstName} {user.lastName}</Text>
                  <Text style={adminStyles.userEmail}>{user.email}</Text>
                  <Text style={[adminStyles.userRole, { color: userType.color }]}>{userType.label}</Text>
                  <TesterBadge user={user} style={{ marginTop: 4 }} />
                  <Text style={[adminStyles.detailText, { fontSize: 11, color: colors.textMuted, marginTop: 4 }]}>
                    {online ? 'Online' : 'Offline'} · Joined {formatShortDate(user.createdAt)}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {showFull && (
            <View style={adminStyles.columnMiddle}>
              <View style={[adminStyles.detailedGradesCard, { marginBottom: 12 }]}>
                <Text style={adminStyles.cardTitle}>Details</Text>
                <View style={adminStyles.submissionDetails}>
                  <Text style={adminStyles.detailText}>Last login: {formatDateTime(user.lastLogin)}</Text>
                  <Text style={adminStyles.detailText}>Reports: {contributionCountsFor(contributionCounts, user.id).reports} · Tech specs: {contributionCountsFor(contributionCounts, user.id).techSpecs}</Text>
                  <Text style={adminStyles.detailText}>Saved venues: {user.savedVenues?.length || 0}</Text>
                </View>
              </View>
            </View>
          )}
        </View>
        {renderUserActions(user)}
      </View>
    );
  };

  const renderOrgListRow = (org) => {
    const isExpanded = expandedOrgId === org.id;
    const ownerUser = users.find((u) => u.id === org.ownerId);

    return (
      <View
        key={org.id}
        style={{
          backgroundColor: colors.cardBg,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: colors.cardBorder,
          borderLeftWidth: 4,
          borderLeftColor: '#9B59B6',
          marginBottom: 8,
        }}
      >
        <TouchableOpacity
          onPress={() => setExpandedOrgId(isExpanded ? null : org.id)}
          style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 12 }}
        >
          <AdminUserAvatar user={org} size={40} />
          <View style={{ flex: 1 }}>
            <Text style={{ color: colors.textPrimary, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>
              {org.username || org.title || 'Unnamed Organization'}
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 12, fontFamily: 'VastagoGrotesk' }}>
              {org.ownerEmail || ownerUser?.email || 'No owner'} · {org.members?.length || 0} members
            </Text>
          </View>
          <Text style={{ color: colors.textMuted }}>{isExpanded ? '▾' : '▸'}</Text>
        </TouchableOpacity>
        {isExpanded && (
          <View style={{ paddingHorizontal: 14, paddingBottom: 14, borderTopWidth: 1, borderTopColor: colors.divider }}>
            <Text style={[adminStyles.detailText, { marginTop: 10 }]}>
              {org.bio || org.description || 'No description'}
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {org.isPublic
                ? renderActionBtn('Make Private', '#e74c3c', () => onOrganizationAction(org.id, 'makePrivate'), true)
                : renderActionBtn('Make Public', '#2ecc71', () => onOrganizationAction(org.id, 'makePublic'), true)}
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <ScrollView style={{ width: '100%' }} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Stats */}
      <View
        style={
          Platform.OS === 'web'
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                gap: 10,
                marginBottom: 20,
              }
            : { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 }
        }
      >
        {statItems.map((stat) => (
          <View
            key={stat.label}
            style={{
              backgroundColor: colors.cardBg,
              borderWidth: 1,
              borderColor: colors.cardBorder,
              borderRadius: 10,
              padding: 14,
              alignItems: 'center',
              minWidth: 120,
            }}
          >
            <Text style={{ color: colors.statAccent || '#0bb5df', fontSize: 22, fontWeight: '800', fontFamily: 'VastagoGrotesk' }}>
              {stat.value}
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 11, fontFamily: 'VastagoGrotesk', textAlign: 'center', marginTop: 4 }}>
              {stat.label}
            </Text>
          </View>
        ))}
      </View>

      {duplicateGroups.length > 0 && (
        <View style={{
          backgroundColor: colors.cardBg,
          borderWidth: 1,
          borderColor: '#7f8c8d',
          borderRadius: 10,
          padding: 14,
          marginBottom: 16,
        }}>
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontWeight: '700', marginBottom: 6 }}>
            Same email, more than one profile
          </Text>
          <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13, marginBottom: 8 }}>
            Archive extras from the approved profile. That only hides the extra Firestore docs. It does not disable their email login.
          </Text>
          {duplicateGroups.map((group) => (
            <Text key={group.email} style={{ color: colors.textMuted, fontFamily: 'VastagoGrotesk', fontSize: 12 }}>
              {group.email} · {group.users.length} profiles
            </Text>
          ))}
        </View>
      )}

      {/* View + columns toolbar */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '600' }}>
            View
          </Text>
          {[
            { id: 'list', label: 'List' },
            { id: 'cards', label: 'Cards' },
          ].map((mode) => (
            <TouchableOpacity
              key={mode.id}
              style={pillStyle(viewMode === mode.id, '#2ecc71')}
              onPress={() => setViewMode(mode.id)}
            >
              <Text style={pillTextStyle(viewMode === mode.id)}>{mode.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {viewMode === 'cards' && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '600' }}>
              Columns
            </Text>
            {[1, 2, 3].map((n) => (
              <TouchableOpacity
                key={n}
                style={columnBtnStyle(columnCount === n)}
                onPress={() => setColumnCount(n)}
              >
                <Text style={{ color: columnCount === n ? '#fff' : colors.textPrimary, fontWeight: '700' }}>{n}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {/* Search */}
      <TextInput
        nativeID={USERS_SEARCH_ID}
        style={{
          backgroundColor: colors.inputBg,
          borderRadius: 8,
          padding: 15,
          color: colors.inputText,
          fontFamily: 'VastagoGrotesk',
          fontSize: 16,
          width: '100%',
          borderWidth: 1,
          borderColor: colors.inputBorder,
          marginBottom: 16,
        }}
        placeholder="Search by name, username, or email..."
        placeholderTextColor={searchPlaceholderColor}
        value={searchQuery}
        onChangeText={setSearchQuery}
      />

      {/* Status filters */}
      <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 10 }}>
        Status
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        {[
          { id: 'all', label: 'All', color: '#0bb5df' },
          { id: 'unactivated', label: 'Unactivated', color: '#f39c12' },
          { id: 'active', label: 'Active', color: '#2ecc71' },
          { id: 'suspended', label: 'Suspended', color: '#e74c3c' },
          { id: 'duplicates', label: 'Duplicate emails', color: '#7f8c8d' },
          { id: 'testers', label: 'Testers', color: TESTER_COLOR },
        ].map((f) => (
          <TouchableOpacity key={f.id} style={pillStyle(statusFilter === f.id, f.color)} onPress={() => setStatusFilter(f.id)}>
            <Text style={pillTextStyle(statusFilter === f.id)}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {statusFilter === 'testers' && (
        <View style={{ backgroundColor: colors.cardBg, borderWidth: 1, borderColor: colors.cardBorder, borderRadius: 12, padding: 14, marginBottom: 16, maxWidth: 520 }}>
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 4 }}>
            Sign in as a tester
          </Text>
          <Text style={{ color: colors.textSecondary || colors.textPrimary, fontSize: 12, marginBottom: 10 }}>
            Switches this browser to that account. To come back to your own account, sign in again.
          </Text>
          <TesterSwitchButtons currentEmail={signedInUser?.email} isDarkMode={isDarkMode} />
        </View>
      )}

      {/* Type filters */}
      <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 10 }}>
        User type
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <TouchableOpacity style={pillStyle(typeFilter === 'all')} onPress={() => setTypeFilter('all')}>
          <Text style={pillTextStyle(typeFilter === 'all')}>All Types</Text>
        </TouchableOpacity>
        {Object.values(userTypes).map((type) => (
          <TouchableOpacity
            key={type.value}
            style={pillStyle(typeFilter === type.value, type.color)}
            onPress={() => setTypeFilter(type.value)}
          >
            <Text style={pillTextStyle(typeFilter === type.value)}>{type.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Sort */}
      <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 10 }}>
        Sort by
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <TouchableOpacity style={pillStyle(sortBy === 'signup', '#9b59b6')} onPress={() => setSortBy('signup')}>
          <Text style={pillTextStyle(sortBy === 'signup')}>Newest Sign Ups</Text>
        </TouchableOpacity>
        <TouchableOpacity style={pillStyle(sortBy === 'lastLogin', '#9b59b6')} onPress={() => setSortBy('lastLogin')}>
          <Text style={pillTextStyle(sortBy === 'lastLogin')}>Recent Login</Text>
        </TouchableOpacity>
      </View>

      <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 14, marginBottom: 20 }}>
        {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'} found
        {organizations.length > 0 ? ` · ${organizations.length} organization${organizations.length === 1 ? '' : 's'}` : ''}
      </Text>

      {/* Organizations */}
      {organizations.length > 0 && (
        <View style={{ marginBottom: 24 }}>
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 18, fontWeight: '700', marginBottom: 12 }}>
            Organizations
          </Text>
          {organizations.map(renderOrgListRow)}
        </View>
      )}

      {/* Users */}
      <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 18, fontWeight: '700', marginBottom: 12 }}>
        Users
      </Text>

      {filteredUsers.length === 0 ? (
        <Text style={adminStyles.emptyStateText}>No users match your filters</Text>
      ) : viewMode === 'list' ? (
        <View>
          {Platform.OS === 'web' && (
            <View style={{
              flexDirection: 'row',
              paddingHorizontal: 14,
              paddingVertical: 8,
              marginBottom: 4,
              gap: 12,
            }}>
              <Text style={{ flex: 1, minWidth: 180, color: colors.textMuted, fontSize: 11, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>USER</Text>
              <Text style={{ minWidth: 100, color: colors.textMuted, fontSize: 11, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>TYPE</Text>
              <Text style={{ minWidth: 90, color: colors.textMuted, fontSize: 11, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>STATUS</Text>
              <Text style={{ minWidth: 100, color: colors.textMuted, fontSize: 11, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>LAST LOGIN</Text>
            </View>
          )}
          {filteredUsers.map(renderUserListRow)}
        </View>
      ) : (
        <View style={cardsGridStyle}>
          {filteredUsers.map((user) => (
            <View key={user.id} style={cardWrapStyle}>
              {renderUserCard(user)}
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}
